import { NextPage } from 'next'
import classNames from 'classnames'

import StoreLayout from './../components/StoreLayout'

import { t } from '../../../utils'
import { staticProps } from '../../../utils/createApolloClient'
import {
  FiltersState,
  filterStateToQueryVariables,
  initalFiltersState
} from '../../songs'
import { STORE_SONGS_QUERY } from '../../../gql'
import { createContext, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { useLazyQuery } from '@apollo/client'
import useScrollLoading from '../../../utils/hooks/useScrollLoading'

import styles from './_storeSongsPage.module.scss'
import StoreSong from './components/StoreSong'
import TryItBlock from '../components/TryItBlock'
import StoreListHeader from '../components/StoreListHeader'
import isServer from '../../../utils/isServer'
import { Song, SortSongsEnum } from '../../../__generated__/graphql'
import { useAuth } from '../../../utils/auth'

export interface StoreSongPageProps {
  songs: Song[]
}
export const SearchContext = createContext(null)
const StoreSongsPage: NextPage<StoreSongPageProps> & {
  theme?: string
} = ({ songs }) => {
  const [filtersState, setFiltersState] = useState<FiltersState>({
    ...initalFiltersState
  })
  const doNewDataRequest = useRef(false)
  const [uploadSongPageNumber, setUploadSongPageNumber] = useState(1)
  const songsListContainerRef = useRef(null)
  const [searchResult, setSearchResult] = useState(songs || [])
  const initialLoad = useRef(true)

  const router = useRouter()
  const { currentUser } = useAuth()

  const doSearch = (filterState) => {
    performSearch({ variables: filterStateToQueryVariables(filterState) })
  }

  const [performSearch, { data: performSearchData }] = useLazyQuery(
    STORE_SONGS_QUERY,
    {
      onCompleted: (data) => {
        if (!isServer()) {
          window.sessionStorage.setItem(
            'storeSongsNumber',
            String(data.storeSongs.length)
          )
          const storeScrollPosition = window.sessionStorage.getItem(
            'storeScrollPosition'
          )
          if (storeScrollPosition) {
            setTimeout(() => {
              window.scroll({
                top: +storeScrollPosition,
                behavior: 'smooth'
              })
              window.sessionStorage.removeItem('storeScrollPosition')
              window.sessionStorage.removeItem('storeSongsNumber')
            }, 300)
          }
        }
      }
    }
  )

  useEffect(() => {
    const storeSongsNumber = window.sessionStorage.getItem('storeSongsNumber')
    const storeScrollPosition = window.sessionStorage.getItem(
      'storeScrollPosition'
    )
    if (storeSongsNumber && storeScrollPosition)
      doSearch({ ...filtersState, perPage: storeSongsNumber }) //query previos number of pages if back from other route

    const onRouteChangeStart = () => {
      window.sessionStorage.setItem(
        'storeScrollPosition',
        window.scrollY.toString()
      )
    }

    router.events.on('routeChangeStart', onRouteChangeStart)

    return () => {
      router.events.off('routeChangeStart', onRouteChangeStart)
    }
  }, [])

  useEffect(() => {
    const hasQueryParams = Object.keys(router.query).length
    if (!initialLoad.current || hasQueryParams || currentUser) {
      doSearch({ ...filtersState, page: uploadSongPageNumber })
    } else initialLoad.current = false
  }, [filtersState, currentUser])

  useScrollLoading(
    songsListContainerRef,
    () =>
      loadNewDataInBottomPosition(
        performSearchData?.storeSongs.length || searchResult.length
      ),
    [performSearchData?.storeSongs]
  )

  const loadNewDataInBottomPosition = (songsLength) => {
    if (
      !doNewDataRequest.current &&
      songsLength > 0 &&
      songsLength % filtersState.perPage == 0
    ) {
      setUploadSongPageNumber((prev) => prev + 1)
      setFiltersState((prevState) => ({
        ...prevState,
        page: uploadSongPageNumber
      }))

      doSearch(filtersState)
    }
    doNewDataRequest.current = true
  }

  useEffect(() => {
    if (performSearchData) {
      setSearchResult(performSearchData.storeSongs as Song[])
      doNewDataRequest.current = false
    }
  }, [performSearchData?.storeSongs])

  useEffect(() => {
    const queryParams: Partial<FiltersState> = {}
    const paramsFromQuery = router.query || {}

    if (paramsFromQuery.q !== undefined) {
      queryParams.query = paramsFromQuery.q as string
    }
    if (paramsFromQuery.orderBy) {
      queryParams.orderBy = paramsFromQuery.orderBy as SortSongsEnum
    }

    if (Object.keys(queryParams).length !== 0) {
      setFiltersState(() => ({
        ...initalFiltersState,
        ...queryParams
      }))
    }
  }, [router.query])

  return (
    <StoreLayout pageTitle={t('WorshipOnline - Songs store')}>
      <SearchContext.Provider
        value={{
          filtersState: filtersState,
          setFiltersState: setFiltersState
        }}
      >
        <div className={classNames('section', styles.storeSongsPage)}>
          <div className="container">
            <StoreListHeader
              svgPath="../../images/store/songs.svg"
              title="All Songs"
              searchContext={SearchContext}
            />
            <div
              ref={songsListContainerRef}
              className="columns is-7-desktop is-variable is-multiline"
            >
              {searchResult.map((song, index) => (
                <div
                  className="column is-4-tablet is-3-widescreen"
                  key={song.id}
                >
                  <StoreSong song={song} priority={index === 0} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </SearchContext.Provider>
      <TryItBlock />
    </StoreLayout>
  )
}

export const getStaticProps = staticProps({
  additionalProps: async ({ client }) => {
    const {
      data: { storeSongs }
    } = await client.query({
      query: STORE_SONGS_QUERY,
      variables: filterStateToQueryVariables(initalFiltersState)
    })

    return {
      songs: storeSongs
    }
  }
})
StoreSongsPage.theme = 'dark'
export default StoreSongsPage
